import { BUILD_DATE } from '../version';
import { DATABASE_SERVICE_URL } from '../services/workerUrls';
import { SN76489 } from '../lib/audio';

export interface BootResult {
  output: string;
}

type OnUpdateCallback = (output: string) => void;

// Simple delay helper
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Global sound chip instance for boot sequence
let soundChip: SN76489 | null = null;

// Initialize sound chip (call once)
async function initSoundChip(): Promise<void> {
  if (!soundChip) {
    soundChip = new SN76489();
    try {
      await soundChip.init();
    } catch (err) {
      // Silently fail if audio context can't be created
      console.warn('Failed to initialize sound chip:', err);
      soundChip = null;
    }
  }
}

// Play BBC Micro startup beep
async function playStartupBeep(): Promise<void> {
  try {
    await initSoundChip();
    if (soundChip) {
      await soundChip.startupBeep();
    }
  } catch (err) {
    // Silently fail - don't block boot sequence
    console.warn('Failed to play startup beep:', err);
  }
}

/**
 * Runs a simplified boot sequence showing just the banner, player initialization, and command prompt.
 * @param onUpdate Callback for live updates
 * @param playerId Player ID for initialization (required - no default)
 */
export async function runBootSequence(onUpdate?: OnUpdateCallback, playerId?: string): Promise<BootResult> {
  if (!playerId) {
    throw new Error('playerId is required for boot sequence');
  }
  const pid = playerId;
  const lines: string[] = [];

  const addLine = (line: string) => {
    lines.push(line);
    onUpdate?.(lines.join('\n'));
  };

  // Play BBC Micro startup beep (non-blocking)
  playStartupBeep();

  // Banner - BBC Micro style (left aligned, simple)
  addLine('SPACE ANTICS SHIPBOARD COMPUTER');
  addLine(`Built: ${new Date(BUILD_DATE === 'development' ? Date.now() : BUILD_DATE).toLocaleString()}`);
  addLine('Copyright (c) 2847 Galactic Systems Corp.');

  await delay(100);

  // Initializing message
  addLine('Initializing shipboard systems...');

  await delay(100);

  // Hardware specs
  addLine('[  0.000] Quantum Memory: 256 TB detected');
  addLine('[  0.001] Neural Processing Unit: Online');
  addLine('[  0.002] Hyperdrive Interface: Standby');

  await delay(200);

  // Player initialization
  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${pid}`, {
      signal: AbortSignal.timeout(5000)
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        addLine(`Welcome back, ${data.data.display_name || 'Commander'}.`);
      }
    }
  } catch (err) {
    // Silently fail - just skip the welcome message
    addLine('Player profile loaded.');
  }

  addLine('');
  addLine('ship:~$');

  return {
    output: lines.join('\n'),
  };
}
